/**
 * Agent API Validators
 *
 * Validators for agent-related API requests and responses
 *
 * @module validators/agent
 */

import { z } from 'zod';
import type { CreateAgentRequest } from '../contracts/rest-api.contract';

/**
 * Validates CreateAgentRequest input
 *
 * @param data Unknown data to validate
 * @returns Validated CreateAgentRequest
 * @throws ZodError if validation fails
 */
export const CreateAgentRequestValidator = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1024).optional(),
  model_id: z.string().min(1),
  instructions: z.array(z.string()).optional(),
  tools: z
    .array(
      z.object({
        name: z.string().min(1),
        description: z.string(),
        schema: z.string(),
      }),
    )
    .optional(),
});

export function validateCreateAgentRequest(data: unknown): CreateAgentRequest {
  return CreateAgentRequestValidator.parse(data);
}

/**
 * Type guard for CreateAgentRequest
 *
 * @param data Unknown data to check
 * @returns true if data is CreateAgentRequest
 */
export function isCreateAgentRequest(data: unknown): data is CreateAgentRequest {
  try {
    CreateAgentRequestValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safe parser that returns validation error details
 *
 * @param data Unknown data to parse
 * @returns { success: true; data } or { success: false; error }
 */
export function safeParseCreateAgentRequest(
  data: unknown,
): { success: true; data: CreateAgentRequest } | { success: false; error: z.ZodError } {
  const result = CreateAgentRequestValidator.safeParse(data);
  return result;
}
